"use client";
import { Card, CardContent, CardHeader } from "./card-pricing";
import { Sparkles as SparklesComp } from "./sparkles-pricing";
import { TimelineContent } from "./timeline-content-pricing";
import { VerticalCutReveal } from "./vertical-cut-reveal-pricing";
import NumberFlow from "@number-flow/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import "./pricing-section.css";
import "./pricing-section-azure-migration.css";



interface PricingSectionAzureMigrationProps {
  openContactModal?: () => void;
}


export default function PricingSectionAzureMigration({ openContactModal }: PricingSectionAzureMigrationProps) {
  const { t, i18n } = useTranslation();
  const pricingRef = useRef<HTMLDivElement>(null);

  const plans = [
    {
      name: t('pricingMigrationCloud.audit.title', "Assessment Migration Azure"),
      description: t('pricingMigrationCloud.audit.description', "Évaluation complète et plan de migration"),
      price: 3000,
      buttonText: t('pricingMigrationCloud.common.contactButton', "Planifier un rendez-vous"),
      buttonVariant: "outline" as const,
      hasGlowEffect: true,
      includes: [
        t('pricingMigrationCloud.audit.features.1', "Plan de migration vers Azure"),
        t('pricingMigrationCloud.audit.features.2', "Cartographie & dépendances"),
        t('pricingMigrationCloud.audit.features.4', "Roadmap par vagues"),
        t('pricingMigrationCloud.audit.features.5', "Estimation budgétaire & FinOps"),
        t('pricingMigrationCloud.audit.features.6', "Présentation \"Executive Summary\" pour les décideurs"),
      ],
    },
    {
      name: t('pricingMigrationCloud.migration.title', "Azure Landing Zone"),
      description: t('pricingMigrationCloud.migration.description', "Azure Landing Zone sécurisée by design"),
      price: 20000,
      buttonText: t('pricingMigrationCloud.common.contactButton', "Planifier un rendez-vous"),
      buttonVariant: "default" as const,
      popular: true,
      hasGlowEffect: false,
      includes: [
        t('pricingMigrationCloud.migration.environment', "Environnement Azure prêt production"),
        t('pricingMigrationCloud.migration.assessment', "Management groups & abonnements"),
        t('pricingMigrationCloud.migration.foundation', "Socle Azure Landing Zone"),
        t('pricingMigrationCloud.migration.identity', "Aligné Cloud Adoption Framework"),
        t('pricingMigrationCloud.migration.rbac', "Microsoft Entra ID & RBAC (MFA/SSO)"),
        t('pricingMigrationCloud.migration.network', "Réseau hub-spoke + hybride"),
        t('pricingMigrationCloud.migration.governance', "Guardrails Policy + logs/alertes"),
        t('pricingMigrationCloud.migration.iac', "IaC (Bicep/Terraform) + handover/runbooks"),
      ],
    },
    {
      name: t('pricingMigrationCloud.enterprise.title', "Migration Azure par vague"),
      description: t('pricingMigrationCloud.enterprise.description', "Solution sur mesure pour grandes entreprises avec besoins spécifiques"),
      price: t('pricingMigrationCloud.common.pricing.onQuote', "Sur devis"),
      buttonText: t('pricingMigrationCloud.common.contactButton', "Planifier un rendez-vous"),
      buttonVariant: "outline" as const,
      hasGlowEffect: false,
      includes: [
        t('pricingMigrationCloud.enterprise.wave', "Vague migrée en production"),
        t('pricingMigrationCloud.enterprise.apps', "1–2 apps ou 10–20 VM par vague"),
        t('pricingMigrationCloud.enterprise.replication', "Réplication ASR, tests de restauration"),
        t('pricingMigrationCloud.enterprise.cutover', "Cutover quasi-zero, rollback testé"),
        t('pricingMigrationCloud.enterprise.postcheck', "Vérifs post-bascule, hypercare J+30"),
        t('pricingMigrationCloud.enterprise.finops', "FinOps J1 : budgets, alertes, réservations"),
        t('pricingMigrationCloud.enterprise.prerequis', "Prérequis : Azure Landing Zone conforme CAF (non incluse)"),
      ],
    },
  ];

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.1,
        duration: 0.2,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };


  return (
    <div
      className="tw-mx-auto tw-relative tw-bg-black tw-overflow-hidden"
      ref={pricingRef}
    >
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-top-0 tw-h-96 tw-w-screen tw-overflow-hidden [mask-image:radial-gradient(50%_50%,white,transparent)]"
      >
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-bg-[linear-gradient(to_right,#ffffff2c_1px,transparent_1px),linear-gradient(to_bottom,#3a3a3a01_1px,transparent_1px)] tw-bg-[size:70px_80px]"></div>
        <SparklesComp
          density={1800}
          speed={1}
          color="#FFFFFF"
          className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-full tw-w-full [mask-image:radial-gradient(50%_50%,white,transparent_85%)]"
        />
      </TimelineContent>
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-left-0 tw-top-[-114px] tw-w-full tw-h-[100vh] tw-flex tw-flex-col tw-items-start tw-justify-start tw-content-start tw-flex-none tw-flex-nowrap tw-gap-2.5 tw-p-0 tw-z-0 tw-overflow-hidden tw-pointer-events-none"
      >
        <div className="framer-1i5axl2">
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 1"
          ></div>
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 2"
          ></div>
        </div>
      </TimelineContent>

      <article className="tw-text-center tw-mb-6 tw-pt-32 tw-max-w-3xl tw-mx-8 md:tw-mx-auto tw-space-y-2 tw-relative tw-z-50">
        <h2 className="tw-text-4xl tw-font-medium tw-text-white">
          <VerticalCutReveal
            splitBy="words"
            staggerDuration={0.05}
            staggerFrom="first"
            reverse={true}
            containerClassName="tw-justify-center tw-pb-[10px]"
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
              delay: 0,
            }}
          >
{t('pricingMigrationCloud.title', 'Votre migration vers Azure sera')}
          </VerticalCutReveal>
        </h2>

      </article>

      <div
        className="tw-absolute tw-top-0 tw-left-[10%] tw-right-[10%] tw-w-[80%] tw-h-full tw-z-0"
        style={{
          backgroundImage: `
        radial-gradient(circle at center, var(--gradient-color) 0%, transparent 70%)
      `,
          opacity: 0.6,
          mixBlendMode: "multiply",
        }}
      />

      {/* Centered blue gradient halo behind cards */}
      <div
        className="tw-absolute tw-top-[60%] tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-[1400px] tw-h-[800px] tw-z-10 tw-pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, rgba(49, 49, 245, 0.6) 0%, rgba(49, 49, 245, 0.3) 40%, rgba(49, 49, 245, 0.1) 70%, transparent 100%)`,
          filter: "blur(70px)",
          borderRadius: "50%",
        }}
      />

      <div className="tw-grid md:tw-grid-cols-3 tw-max-w-5xl tw-gap-4 tw-pb-6 tw-mx-8 md:tw-mx-auto tw-relative tw-z-20">
        {plans.map((plan, index) => (
          <TimelineContent
            key={plan.name}
            as="div"
            animationNum={3 + index}
            timelineRef={pricingRef}
            customVariants={revealVariants}
            once={true}
          >
            <Card
              className={`tw-relative tw-text-white tw-h-full ${
                plan.popular
                  ? "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-shadow-[0px_-13px_300px_0px_#0900ff] tw-z-20"
                  : index === 0
                    ? "tw-border-neutral-800/80 tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-z-20"
                    : "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-z-10"
              }`}
            >
              <CardHeader className="tw-text-left" style={index === 2 ? { paddingBottom: '31px' } : {}}>
                <div className="tw-flex tw-justify-between">
                  <h3 className={`tw-text-3xl tw-mb-2 ${index === 1 || index === 2 ? 'tw-max-w-[250px]' : ''}`}>{plan.name}</h3>
                </div>
                <div className="tw-text-sm tw-text-gray-400 tw-mb-1 tw-font-[550]" style={{ fontSize: '20px', transform: 'translateY(7px)' }}>
                  {index === 0 ? t('pricingMigrationCloud.common.pricing.fixed', 'Forfait fixe') :
                   index === 2 ? t('pricingMigrationCloud.common.pricing.quote', 'Tarification') :
                   t('pricingMigrationCloud.common.pricing.from', 'À partir de')}
                </div>
                <div className="tw-flex tw-items-baseline">
                  <>
                    <span className={`tw-text-4xl tw-font-semibold ${index === 2 ? 'tw-pt-[7px]' : ''}`}>
                      {typeof plan.price === 'string' ? (
                        <span className="tw-text-4xl tw-font-semibold">
                          {plan.price}
                        </span>
                      ) : (
                        <>
                          <NumberFlow
                            format={{
                              currency: "EUR",
                            }}
                            value={plan.price}
                            className="tw-text-4xl tw-font-semibold"
                          />
                          &nbsp;€<sup className="tw-text-sm tw-text-gray-400 tw-ml-1" style={{ position: 'relative', top: '-16px' }}>{t('pricingMigrationCloud.common.taxInfo', 'HT')}</sup>
                        </>
                      )}
                    </span>
                    {typeof plan.price !== 'string' && index !== 0 && (
                      <span className="tw-text-gray-300 tw-ml-2" style={index === 1 ? { transform: 'translateY(-1px)', color: 'rgb(156 163 175 / var(--tw-text-opacity, 1))', fontWeight: '550', fontSize: '13px' } : {}}>
                        {index === 1 ? (i18n.language !== 'en' ? t('pricingMigrationCloud.common.pricing.perScope', 'selon périmètre') : '') :
                         t('pricingMigrationCloud.common.pricing.perProject', '/ projet')}
                      </span>
                    )}
                  </>
                </div>

              </CardHeader>

              <CardContent className="tw-pt-0">
                <button
                  onClick={() => {
                    openContactModal?.();
                  }}
                  className={`tw-w-full tw-mb-6 tw-p-4 tw-text-xl tw-rounded-xl ${
                    plan.popular
                      ? "tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600 tw-shadow-lg tw-shadow-blue-800 tw-border tw-border-blue-500 tw-text-white"
                      : index === 0
                        ? "tw-bg-gradient-to-t tw-from-neutral-950/50 tw-to-neutral-600/50 tw-backdrop-blur-sm tw-shadow-lg tw-shadow-neutral-900/30 tw-border tw-border-neutral-800/60 tw-text-white"
                        : plan.buttonVariant === "outline"
                          ? "tw-bg-gradient-to-t tw-from-neutral-950 tw-to-neutral-600 tw-shadow-lg tw-shadow-neutral-900 tw-border tw-border-neutral-800 tw-text-white"
                          : ""
                  }`}
                >
                  {plan.buttonText}
                </button>

                <div className="tw-space-y-3 tw-pt-[2px] tw-border-t tw-border-neutral-700">
                  <div className="tw-text-sm tw-text-gray-400 tw-mb-1 tw-font-[550]" style={{ fontSize: '20px', transform: 'translateY(4px)' }}>
                    {index === 0 ? t('pricingMigrationCloud.common.duration.2days', '2 jours') :
                     t('pricingMigrationCloud.common.duration.4weeks', '4 semaines')}
                  </div>
                  <h4 className="tw-font-medium tw-text-base tw-mb-3 tw-mt-[6px]">
                    {plan.includes[0]}
                  </h4>
                  <ul className="tw-space-y-2">
                    {plan.includes.slice(1).map((feature: string, featureIndex: number) => (
                      <li
                        key={featureIndex}
                        className="tw-flex tw-items-start tw-gap-2"
                      >
                        <span className="tw-h-2.5 tw-w-2.5 tw-bg-neutral-500 tw-rounded-full tw-mt-1.5 tw-flex-shrink-0"></span>
                        <span className="tw-text-sm tw-text-gray-300 tw-font-[550] tw-text-left">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TimelineContent>
        ))}
      </div>
    </div>
  );
}